import { Model } from 'infra/model'
import { VoiceCallDto, VoiceCallStatusDto } from 'messaging/dtos/voice-calls'
import { action, computed, makeObservable, observable } from 'mobx'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'

export class VoiceCall extends Model<VoiceCallDto, VoiceCall> {
  id = Id.none

  @observable conversationId = ''
  @observable workspaceId = ''
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()
  @observable status = VoiceCallStatus.Unknown
  @observable callerUserPublicId = ''
  @observable recipientUserPublicId: string | null = null
  @observable twilioCallSid: string | null = null
  @observable dateConnecting: Date | null = null
  @observable dateRinging: Date | null = null
  @observable dateStarted: Date | null = null
  @observable dateCompleted: Date | null = null
  @observable dateRecordingReady: Date | null = null
  @observable twilioRecordingUrl: string | null = null
  @observable recordingUrl: string | null = null
  @observable completedCallDuration: number | null = null

  @observable callerTwilioToken: string | null = null

  constructor() {
    super()
    makeObservable(this)
  }

  @action
  setCallerTwilioToken(token: string) {
    this.callerTwilioToken = token
  }

  @action.bound
  setCallStatus(status: VoiceCallStatus) {
    this.status = status
  }

  @action.bound
  setPrepared(preparedAt: Date) {
    this.status = VoiceCallStatus.Prepared
    this.dateCreated = preparedAt
  }

  @action.bound
  setInProgress(startedAt: Date) {
    this.status = VoiceCallStatus.InProgress
    this.dateStarted = startedAt
  }

  @computed
  get prepared() {
    return this.status === VoiceCallStatus.Prepared
  }

  @computed
  get connecting() {
    return this.status === VoiceCallStatus.Connecting
  }

  @computed
  get ringing() {
    return this.status === VoiceCallStatus.Ringing
  }

  @computed
  get inProgress() {
    return this.status === VoiceCallStatus.InProgress
  }

  @computed
  get completed() {
    return this.status === VoiceCallStatus.Completed
  }

  parse(attrs: Partial<VoiceCallDto>): Partial<VoiceCall> {
    return {
      id: attrs.id,
      conversationId: attrs.conversation_id,
      workspaceId: attrs.workspace_id,
      dateCreated: maybeParseDate(attrs.date_created),
      dateModified: maybeParseDate(attrs.date_modified),
      status: VoiceCall.mapCallStatus(attrs.status),
      callerUserPublicId: attrs.caller_user_public_id,
      recipientUserPublicId: attrs.recipient_user_public_id,
      twilioCallSid: attrs.twilio_call_sid,
      dateConnecting: maybeParseDate(attrs.date_connecting),
      dateRinging: maybeParseDate(attrs.date_ringing),
      dateStarted: maybeParseDate(attrs.date_started),
      dateCompleted: maybeParseDate(attrs.date_completed),
      dateRecordingReady: maybeParseDate(attrs.date_recording_ready),
      twilioRecordingUrl: attrs.recording_url,
      completedCallDuration: attrs.completed_call_duration,
    }
  }

  private static mapCallStatus(
    status: VoiceCallStatusDto | undefined
  ): VoiceCallStatus {
    if (!status) {
      return VoiceCallStatus.Unknown
    }
    return StatusMapping[status]
  }
}

export const enum VoiceCallStatus {
  Unknown = 'Unknown',
  Prepared = 'Prepared',
  Connecting = 'Connecting',
  Ringing = 'Ringing',
  InProgress = 'InProgress',
  Completed = 'Completed',
}

const StatusMapping = {
  [VoiceCallStatusDto.Prepared]: VoiceCallStatus.Prepared,
  [VoiceCallStatusDto.Connecting]: VoiceCallStatus.Connecting,
  [VoiceCallStatusDto.Ringing]: VoiceCallStatus.Ringing,
  [VoiceCallStatusDto.InProgress]: VoiceCallStatus.InProgress,
  [VoiceCallStatusDto.Completed]: VoiceCallStatus.Completed,
}
